/**
 * billingService.js — wrappers over the Django `billing` API.
 *
 * All requests go through the shared `httpClient` (session cookie + CSRF).
 *
 * Endpoints — see `billing/views.py`:
 *   GET    /billing/me                          → billing state + live breakdown
 *   POST   /billing/checkout                    → Stripe Checkout session URL
 *   POST   /billing/portal                      → Stripe Customer Portal URL
 *   POST   /billing/resize                      → modify seats and/or period
 *   PUT    /billing/active-locations            → bulk set the active list
 *   POST   /billing/active-locations            → add one location to the list
 *   DELETE /billing/active-locations/<id>       → remove one location from the list
 */

import i18n from 'i18next';
import httpClient from './httpClient';
import { gaIdsPayload, pushEvent } from '@/lib/ga';

const BASE = '/billing';

/**
 * El idioma que está ENSEÑANDO la app, para que el checkout de Stripe salga en
 * ese mismo. Sin esto el backend usa `preferredLanguage`, y hay cuentas de
 * Portugal guardadas como `es` que pulsan un botón en portugués y aterrizan en
 * un checkout en español. El servidor lo valida contra los idiomas soportados.
 */
function uiLang() {
  return i18n.resolvedLanguage || i18n.language || undefined;
}

/** @typedef {{from: number, to: number, unit_cents: number, line_cents: number, flat: boolean, count: number}} BreakdownLine */

/**
 * GET /billing/me — full billing snapshot for the current user.
 *
 * Returns:
 *   {
 *     plan: 'free' | 'pro',
 *     isPro: boolean,
 *     subscriptionStatus, billingPeriod, trialEnd, currentPeriodEnd,
 *     cancelAtPeriodEnd,
 *     purchasedSeats, maxSeats, availableSeats,
 *     activeLocationIds: string[],
 *     seatedLocationIds: string[],
 *     breakdown: { totalCents, lines },   // live preview for current purchased_seats
 *   }
 */
export async function getBillingMe() {
  const { data } = await httpClient.get(`${BASE}/me`);
  return _normalizeBillingMe(data);
}

function _normalizeBillingMe(d) {
  return {
    plan:                d.plan || 'free',
    isPro:               !!d.is_pro,
    isPayingPro:         !!d.is_paying_pro,
    // Hay una suscripcion de Stripe que se puede modificar. NO es "es Pro":
    // un grandfathered, un enterprise y un comp lo son y no la tienen, asi
    // que ofrecerles ampliar el plan acaba en un 502. Lo decide el backend
    // porque la SPA no recibe los campos con los que decidirlo.
    stripeManaged:       !!d.stripe_managed,
    subscriptionStatus:  d.subscription_status || null,
    billingPeriod:       d.billing_period || null,
    priceFamily:         d.price_family || null,
    seatsPendingChoice:  !!d.seats_pending_choice,
    trialUsed:           !!d.trial_used,
    trialEnd:            d.trial_end || null,
    currentPeriodEnd:    d.current_period_end || null,
    cancelAtPeriodEnd:   !!d.cancel_at_period_end,
    closingMode:         !!d.closing_mode,
    canceledAt:          d.canceled_at || null,
    paymentFailedAt:     d.payment_failed_at || null,
    // Fin de la gracia por impago (null si no hay impago). Lo calcula el
    // backend: la ventana es una decisión de negocio, no de presentación.
    pastDueGraceUntil:   d.past_due_grace_until || null,
    cancellationFeedback: d.cancellation_feedback || null,
    cancellationComment: d.cancellation_comment || null,
    cancellationReason:  d.cancellation_reason || null,
    lastPaymentError:    d.last_payment_error || null,
    purchasedSeats:      d.purchased_seats || 0,
    maxSeats:            d.max_seats || 0,
    availableSeats:      d.available_seats || 0,
    activeLocationIds:   Array.isArray(d.active_location_ids) ? d.active_location_ids : [],
    // Cuales de esos llevan asiento Pro. Llega ya derivada del backend
    // (acotada al cupo), asi que la SPA no tiene que recalcular nada.
    seatedLocationIds:   Array.isArray(d.seated_location_ids) ? d.seated_location_ids : [],
    grandfatheredPro:    !!d.grandfathered_pro,
    enterprise:          !!d.enterprise,
    // Tier breakdown: { total_cents, family, tiers_mode, lines: [...] }.
    // `tiersMode` says how to read `lines`: 'graduated' → one line per tier
    // reached, summed; 'volume' → exactly one line, the whole quantity at a
    // single unit price.
    breakdown: d.breakdown ? {
      family:     d.breakdown.family || null,
      tiersMode:  d.breakdown.tiers_mode || null,
      totalCents: d.breakdown.total_cents || 0,
      lines: (d.breakdown.lines || []).map((l) => ({
        tierIdx:        l.tier_idx,
        count:          l.count,
        unitCents:      l.unit_cents,
        flatCents:      l.flat_cents,
        subtotalCents:  l.subtotal_cents,
      })),
    } : null,
  };
}


/**
 * GET /billing/active-pricing (anonymous, no auth required).
 *
 * Returns the active price family + total cents at each seat count (1..30)
 * for both periods. The backend has already done the tier math — which is not
 * the same formula in every family (graduated for launch/v2, volume for v3) —
 * so cards just look up `totals[period][seats-1]` for the headline price and
 * never reimplement it.
 *
 * Shape:
 *   {
 *     family: 'launch' | 'v3' | 'v4',   // v2 retirada, ya no se publica
 *     trialDays: 7,
 *     annualDiscountPct: 20,   // plano en todas las familias, ver abajo
 *     maxSeats: 30,
 *     totals: { monthly: [t1, ..., t30], annual: [t1, ..., t30] },
 *     families: { launch: {...}, v3: {...} },   // totals de cada familia
 *     regular: null | {
 *       family: 'v3',
 *       totals: { monthly: [...], annual: [...] }
 *     },
 *     anchor: null | {
 *       family: 'v2',
 *       totals: { monthly: [...], annual: [...] }
 *     }
 *   }
 *
 * `annualDiscountPct` es el % que anuncia el badge del toggle anual: hoy es un
 * 20% plano para todas las familias (decisión de producto — mismo número en
 * landing, /pricing y SubscriptionCard), aunque el ahorro real de v3 varíe por
 * conteo de locales (18,70%–19,85%). Renderiza SIEMPRE este valor: si el número
 * anunciado cambia, se cambia en el backend y no en cada card.
 *
 * `regular` is only present when the active family differs from the list-price
 * family (i.e. a promo is running) — lets those cards render the strikethrough
 * "from €X" prices without a second roundtrip. Absent = no promo, no strike.
 * Es además lo que gatea el badge «Precio de lanzamiento» de `BusinessPlanCard`:
 * el badge se pinta si y solo si hay bloque `regular`, así que la variable de
 * entorno que activa una tarifa nueva lo apaga sin tocar código. No lo cambies
 * a un literal de familia: la card no sabe —ni debe saber— qué familia es promo.
 *
 * `anchor` es la tarifa ANTERIOR (v2), la que las cards tachan en el modo
 * mensual. No sale de `families` porque v2 está retirada y `families` solo
 * lista las familias contratables; y no sale de `regular` porque ese bloque
 * responde a "hay una promo corriendo", que hoy no la hay. Ausente = sin
 * tachado. El tachado del modo ANUAL no usa esto: es la tarifa mensual de la
 * propia familia × 12 (ver `annualizedMonthlyCents`).
 */
export async function getActivePricing() {
  const { data } = await httpClient.get(`${BASE}/active-pricing`, {
    params: { ..._browserTimezone(), ..._countryOverride() },
  });
  return {
    // Fallback a la tarifa VIGENTE, no a v2: v2 está retirada y sin Price IDs,
    // así que caer ahí dejaría la card cotizando una familia inexistente.
    family:             data.family || 'v3',
    // `??` (no `||`) — STRIPE_TRIAL_DAYS=0 significa "sin trial" y debe
    // llegar como 0, no caer al fallback. Solo null/undefined caen a 7.
    trialDays:          data.trial_days ?? 7,
    annualDiscountPct:  data.annual_discount_pct || 0,
    maxSeats:           data.max_seats || 30,
    totals:             _normalizeTotals(data.totals),
    // Totals de TODAS las familias configuradas, para poder cotizar a un
    // usuario grandfathered con SUS precios mediante lookup directo. No
    // inferir la familia desde `totals`/`regular`: eso solo funcionaba con
    // exactamente dos familias y una de ellas siempre activa.
    families: Object.fromEntries(
      Object.entries(data.families || {}).map(([fam, totals]) => [
        fam, _normalizeTotals(totals),
      ]),
    ),
    regular: data.regular ? {
      family: data.regular.family,
      totals: _normalizeTotals(data.regular.totals),
    } : null,
    anchor: data.anchor ? {
      family: data.anchor.family,
      totals: _normalizeTotals(data.anchor.totals),
    } : null,
    // Divisa en la que PINTAR los importes: la que enseñará el Checkout a este
    // visitante con Adaptive Pricing. Los `totals` siguen en céntimos de EUR;
    // `formatPrice` convierte al formatear. Sin bloque ⇒ euros, como siempre.
    displayCurrency: _normalizeDisplayCurrency(data.display_currency),
  };
}

function _normalizeDisplayCurrency(d) {
  const rate = Number(d?.rate);
  if (!d || !d.code || !Number.isFinite(rate) || rate <= 0) {
    return { code: 'eur', rate: 1, country: '', approx: false };
  }
  return {
    code: String(d.code).toLowerCase(),
    rate,
    country: d.country || '',
    approx: Boolean(d.approx),
  };
}

/**
 * La zona horaria del dispositivo (`America/Bogota`), para que el backend la
 * cruce con la IP al decidir la divisa. Una VPN cambia la IP pero no la zona:
 * sin esto, un visitante de Colombia con VPN salía en dólares porque su IP
 * estaba en Panamá. Ver `resolve_country` en `billing/local_currency.py`.
 */
function _browserTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz ? { tz } : {};
  } catch {
    return {};
  }
}

/**
 * `?country=CO` en la URL de la PÁGINA se reenvía a la API, para poder ver los
 * precios como los vería un visitante de otro país sin VPN. Solo cambia cómo se
 * pintan, así que no hay nada que proteger.
 */
function _countryOverride() {
  if (typeof window === 'undefined') return {};
  try {
    const country = new URLSearchParams(window.location.search).get('country');
    return country && /^[A-Za-z]{2}$/.test(country) ? { country } : {};
  } catch {
    return {};
  }
}

/**
 * Totals de la familia de un usuario concreto, con fallback a la familia activa
 * cuando no la conocemos (visitante anónimo) o cuando el backend no la expone
 * (familia sin tier table — no debería pasar, lo caza `verify_stripe_prices`).
 *
 * @param {ReturnType<typeof getActivePricing> extends Promise<infer T> ? T : never} pricing
 * @param {string|null|undefined} family
 */
export function totalsForFamily(pricing, family) {
  if (!pricing) return null;
  if (family && pricing.families && pricing.families[family]) {
    return pricing.families[family];
  }
  return pricing.totals;
}

function _normalizeTotals(d) {
  return {
    monthly: Array.isArray(d?.monthly) ? d.monthly.map(Number) : [],
    annual:  Array.isArray(d?.annual)  ? d.annual.map(Number)  : [],
  };
}

/**
 * POST /billing/checkout — body: { seats, period, return_path?,
 * seat_for_location_id? }.
 *
 * `returnPath` is the SPA path to send the user back to after success /
 * cancel (with `?stripe=success|cancel` appended). Falls back to the
 * static SUCCESS/CANCEL paths in Django settings if omitted (decisiones
 * §26.20). Backend validates the path starts with `/`.
 *
 * `seatForLocationId` es el local que motivó la compra. Viaja a la metadata de
 * la suscripción de Stripe y vuelve en el webhook de creación: sin él, el
 * asiento recién comprado cae en el primer local por orden de llegada de
 * Google, que casi nunca es el que el cliente acaba de pulsar.
 *
 * `ctaId` es el CTA que motivó la compra, y viaja por el mismo carril y por el
 * mismo motivo: el alta de plan solo la conoce el webhook, al otro lado de un
 * round-trip completo por Stripe. Mandándolo aquí, el backend lo guarda en la
 * metadata de la suscripción y el webhook le atribuye el alta (decisiones §124).
 * Sin esto solo se sabría cuánta gente pulsa, nunca qué botón consiguió qué
 * contratación. Se valida contra la allowlist en el servidor: aquí es un dato
 * del navegador y como tal no se cree nada de lo que traiga.
 *
 * Returns `{ url }` for the Stripe Checkout session (top-level navigation).
 */
export async function createCheckoutSession({
  seats, period = 'monthly', returnPath, seatForLocationId, ctaId,
}) {
  const body = { seats, period, lang: uiLang() };
  if (returnPath) body.return_path = returnPath;
  if (seatForLocationId) body.seat_for_location_id = seatForLocationId;
  if (ctaId) body.cta_id = ctaId;
  // La PANTALLA desde la que se pulsó, para el desglose por ruta del panel. Se
  // lee aquí de `window.location` y NO se le pide al que llama, igual que hace
  // `getGbusinessConnectUrl` con su `?from=`: doce muros distintos abren el
  // mismo modal y pasarla a mano por doce sitios es doce sitios donde olvidarla
  // — y olvidarla no rompe nada, solo deja la fila muda (§125).
  //
  // Vale leerla en este momento porque el modal no navega: se abre encima de la
  // página del muro, así que la ruta de ahora es la del botón que lo abrió.
  if (ctaId && typeof window !== 'undefined') {
    body.cta_path = window.location?.pathname || '';
  }
  // GA4: con el client_id el webhook manda el trial y el pago a GA4 en nombre
  // de este visitante (ver `src/lib/ga.js`). Sin consentimiento no hay id.
  Object.assign(body, gaIdsPayload());
  pushEvent('begin_checkout', { seats, period });
  const { data } = await httpClient.post(`${BASE}/checkout`, body);
  return data.url;
}

/**
 * GET /billing/geogrid-packs — catálogo de packs de créditos del Scanner SEO.
 *
 * Los importes son NETOS (sin IVA), como la suscripción: el impuesto lo calcula
 * Stripe en el checkout. `purchaseReady` es lo que decide si el botón de comprar
 * existe — fuera de producción los Price IDs no están configurados a propósito,
 * y sin esa comprobación el botón solo devolvería `StripeNotConfigured`.
 */
export async function getGeogridPacks() {
  const { data } = await httpClient.get(`${BASE}/geogrid-packs`);
  return {
    packs: (data.packs || []).map((pack) => ({
      key: pack.key,
      credits: pack.credits,
      amountCents: pack.amount_cents,
      discountPct: pack.discount_pct || 0,
      // 'popular' | 'best_value' | null. Lo decide el catálogo del backend
      // porque es una afirmación sobre los importes.
      badge: pack.badge || null,
    })),
    currency: data.currency || 'eur',
    taxIncluded: !!data.tax_included,
    purchaseReady: !!data.purchase_ready,
  };
}

/**
 * POST /billing/geogrid-packs/checkout — body { pack, return_path? }.
 * Devuelve la URL de Stripe Checkout (navegación de primer nivel).
 *
 * Es una compra ÚNICA (`mode='payment'`), no una suscripción: los créditos
 * comprados no caducan. Requiere ser Pro — un free no puede gastarlos, así que
 * venderle sería cobrarle por algo que no puede usar.
 */
export async function createGeogridPackCheckout({ pack, returnPath }) {
  const body = { pack, lang: uiLang() };
  if (returnPath) body.return_path = returnPath;
  const { data } = await httpClient.post(`${BASE}/geogrid-packs/checkout`, body);
  return data.url;
}

/**
 * POST /billing/reconcile-checkout — red de seguridad para webhooks perdidos.
 * Al volver de Stripe Checkout (?stripe=success&session_id=cs_...), interroga
 * a Stripe por la session y sincroniza el estado al instante, reusando el
 * mismo path idempotente del webhook. Devuelve el billing `me` actualizado.
 * 403 si la session no pertenece al user (manejado por el backend).
 */
export async function reconcileCheckout(sessionId) {
  const { data } = await httpClient.post(`${BASE}/reconcile-checkout`, { session_id: sessionId });
  return _normalizeBillingMe(data);
}

/**
 * GET /billing/invoices — historial de facturas Stripe del usuario.
 * Devuelve las últimas 6 (default del backend). Cada invoice trae los
 * campos planos { id, number, createdAt (epoch s), amountPaid (cents),
 * currency, status, invoicePdf, hostedUrl, ... }. Para free sin
 * stripe_customer_id la lista llega vacía. Usuarios con histórico más
 * largo siguen teniendo acceso completo desde el Customer Portal.
 */
export async function listInvoices() {
  const { data } = await httpClient.get(`${BASE}/invoices`);
  return data.invoices || [];
}


/**
 * POST /billing/cancel — body: { feedback, comment? } (decisiones §26.26).
 * `feedback` debe ser una de las 8 enums Stripe (validado backend); el
 * frontend gatea el submit a una selección de RadioGroup. `comment` es
 * opcional (texto libre <=1000 chars). Devuelve el `me` payload
 * actualizado (cancelAtPeriodEnd=true, currentPeriodEnd, etc.) para
 * que la UI no necesite refetch.
 *
 * Errores posibles (code en `e.response.data.code`):
 *   - invalid_cancellation_feedback (400)
 *   - no_active_subscription (409)
 *   - already_canceling (409) — race entre tabs; SPA debe refetch.
 *   - stripe_api_error (502)
 */
export async function cancelSubscription({ feedback, comment }) {
  const body = { feedback };
  if (comment) body.comment = comment;
  const { data } = await httpClient.post(`${BASE}/cancel`, body);
  return data;
}


/**
 * POST /billing/reactivate — sin body. Deshace `cancel_at_period_end`.
 * Reemplaza el "Renew subscription" del Customer Portal porque
 * deshabilitar `subscription_cancel` en la portal config también apaga
 * ese flujo (decisiones §26.26). Devuelve el `me` payload actualizado.
 *
 * Errores posibles:
 *   - no_active_subscription (409)
 *   - not_canceling (409) — sub no estaba marcada para cancelar.
 *   - stripe_api_error (502)
 */
export async function reactivateSubscription() {
  const { data } = await httpClient.post(`${BASE}/reactivate`, {});
  return data;
}


/**
 * POST /billing/end-trial — sin body. Termina el periodo de prueba de
 * inmediato (Stripe `trial_end=now`) y cobra ya con la tarjeta del checkout.
 * Alimenta el CTA "Pagar ahora" (sección de backfill y Ajustes → Suscripción).
 *
 * Devuelve `{ converted, subscriptionStatus, isPro, isPayingPro }`. **Hay que
 * mirar `converted`**: el endpoint responde 200 aunque el cobro rebote, porque
 * el trial SÍ terminó. Un 4xx haría creer que no pasó nada, que es lo contrario
 * de la verdad. Con `converted === false` el cargo no está confirmado (tarjeta
 * rechazada, autenticación pendiente, o un método diferido tipo SEPA) y quien
 * llama debe decirlo, nunca cantar éxito.
 *
 * Errores posibles:
 *   - no_active_subscription (409)
 *   - not_trialing (409) — la sub no está en trial.
 *   - subscription_canceling (409) — hay una cancelación pendiente; reactivar antes.
 *   - no_payment_method (409) — sin tarjeta con la que cobrar.
 *   - stripe_api_error (502)
 */
export async function endTrialNow() {
  const { data } = await httpClient.post(`${BASE}/end-trial`, {});
  return {
    converted: !!data.converted,
    subscriptionStatus: data.subscription_status || null,
    isPro: !!data.is_pro,
    isPayingPro: !!data.is_paying_pro,
  };
}


/**
 * GET /billing/end-trial/preview — what "pagar ahora" would charge, from
 * Stripe (tax + proration accurate).
 * @returns {Promise<{available:boolean, nowCents:number, recurringCents:number,
 *   currency:string, period:string, prorated:boolean}>}
 */
export async function getEndTrialPreview() {
  const { data } = await httpClient.get(`${BASE}/end-trial/preview`);
  return {
    available: !!data.available,
    nowCents: data.now_cents || 0,
    recurringCents: data.recurring_cents || 0,
    currency: data.currency || 'EUR',
    period: data.period || 'monthly',
    prorated: !!data.prorated,
  };
}


/**
 * POST /billing/portal — returns `{ url }` for the Customer Portal session.
 * `returnPath` is where Stripe sends the user back to after they close the
 * portal. Falls back to `STRIPE_PORTAL_RETURN_PATH` when omitted.
 * The portal has "Update subscription items / quantity" disabled in dashboard
 * config: resize + period switch live in our `/billing/resize` endpoint.
 */
export async function createPortalSession({ returnPath } = {}) {
  const body = {};
  if (returnPath) body.return_path = returnPath;
  const { data } = await httpClient.post(`${BASE}/portal`, body);
  return data.url;
}

/**
 * POST /billing/resize — body: { seats?, period?, seat_for_location_id? }.
 * At least one de `seats`/`period` es obligatorio.
 *
 * `seatForLocationId` es el local que MOTIVO la compra. Sin el, el asiento
 * recien pagado se reparte por orden de llegada y acaba en otro local: el
 * backend rellena los huecos con `ensure_seats_assigned` antes de que la SPA
 * pueda decir nada. Va en la misma peticion y no en una segunda para que no
 * haya una ventana en la que el cliente ha pagado y el asiento esta mal puesto.
 *
 * 409 codes the caller must handle:
 *   - 'downsize_blocked'      → detail includes { active_count } (number of
 *                               active locations that exceed the new seats)
 *   - 'enterprise_cap_exceeded'
 *
 * 402 'resize_payment_incomplete': el prorrateo se cobra en el momento y el
 * cambio solo se aplica si ese cobro sale. Tarjeta rechazada o 3DS pendiente →
 * el plan NO cambia y el cuerpo trae `hosted_invoice_url`, la página de Stripe
 * donde pagar la factura (al pagarla, Stripe aplica el cambio). Ver
 * `toastResizePaymentIncomplete` en `@/lib/resizePayment`.
 *
 * Returns the new billing state ({ purchased_seats, billing_period, breakdown }).
 *
 * Los tres campos son opcionales y el cuerpo solo lleva los que vengan: quien
 * amplia en bloque manda `seats` a secas, porque el reparto lo fija despues con
 * `setSeatedLocations` y el periodo no cambia.
 *
 * @param {{ seats?: number, period?: string, seatForLocationId?: string }} opciones
 */
export async function resizeSubscription({ seats, period, seatForLocationId }) {
  const body = {};
  if (seats != null) body.seats = seats;
  if (period != null) body.period = period;
  if (seatForLocationId) body.seat_for_location_id = seatForLocationId;
  const { data } = await httpClient.post(`${BASE}/resize`, body);
  return _normalizeBillingMe(data);
}

/**
 * PUT /billing/active-locations — bulk set.
 * Body: { location_ids: [...] }. Returns the updated list.
 */
export async function setActiveLocations(locationIds) {
  const { data } = await httpClient.put(`${BASE}/active-locations`, {
    location_ids: locationIds || [],
  });
  return Array.isArray(data?.active_location_ids) ? data.active_location_ids : [];
}

/**
 * POST /billing/active-locations — add a single location_id.
 * 409 `no_seats_available` when the user is at cap.
 */
export async function addActiveLocation(locationId) {
  const { data } = await httpClient.post(`${BASE}/active-locations`, {
    location_id: locationId,
  });
  return Array.isArray(data?.active_location_ids) ? data.active_location_ids : [];
}

/**
 * POST /billing/seated-locations/<location_id> — le da uno de los asientos Pro
 * LIBRES a este local.
 *
 * Es la salida del 402 `seat_required`. Con el cupo lleno el backend contesta
 * 409 `no_seats_available` y entonces sí toca ofrecer ampliar el plan: este
 * endpoint reparte lo ya contratado, no vende nada.
 */
export async function assignSeat(locationId) {
  const { data } = await httpClient.post(
    `${BASE}/seated-locations/${encodeURIComponent(locationId)}`,
  );
  return _normalizeBillingMe(data);
}

/**
 * PUT /billing/seated-locations - reparte de golpe los asientos Pro.
 *
 * La hermana en bloque de `assignSeat`, y la que fija el ORDEN: el primero de
 * la lista es "el local principal" (recap mensual, PDF agregado, baja).
 *
 * Pasarse del cupo devuelve 409 `no_seats_available`; un local que no este
 * activo, 400 `invalid_active_location`.
 */
export async function setSeatedLocations(locationIds) {
  const { data } = await httpClient.put(`${BASE}/seated-locations`, {
    location_ids: locationIds,
  });
  return _normalizeBillingMe(data);
}

/**
 * DELETE /billing/active-locations/<location_id>.
 * Idempotent — succeeds even if the location was not in the list.
 */
export async function removeActiveLocation(locationId) {
  const { data } = await httpClient.delete(
    `${BASE}/active-locations/${encodeURIComponent(locationId)}`,
  );
  return Array.isArray(data?.active_location_ids) ? data.active_location_ids : [];
}
